import { formatDateTime } from "../utils/formatters";
import api from "../../../utilities/Interceptor"; // adjust path to match your existing import
import { useState } from "react";

const STATUS_BADGE = {
  accepted: "p-badge--green",
  completed: "p-badge--green",
  sent: "p-badge--blue",
  pending: "p-badge--amber",
  rejected: "p-badge--neutral",
  cancelled: "p-badge--neutral",
};

export default function DownlineTab({
  downline,
  loading,
  refCode,
  requestsById,
  setRequestsById,
  expandedId,
  setExpandedId,
}) {
  const [loadingRequests, setLoadingRequests] = useState(null);
  const [requestError, setRequestError] = useState(null);

  const handleToggle = async (driverId) => {
    if (expandedId === driverId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(driverId);
    setRequestError(null);

    // already cached (persists across tab switches now) — don't refetch
    if (requestsById[driverId]) return;

    setLoadingRequests(driverId);
    try {
      const { data } = await api.get("/driver/web/downline/requests", {
        params: { driver_id: driverId, ref_code: refCode },
      });
      if (data.success) {
        console.log('in dw', data)
        setRequestsById((prev) => ({ ...prev, [driverId]: data.requests || [] }));
      }
    } catch (err) {
      console.error("Error fetching driver requests:", err);
      setRequestError(driverId);
    } finally {
      setLoadingRequests(null);
    }
  };

  if (loading) {
    return (
      <div className="p-downline-list" role="tabpanel">
        {[...Array(3)].map((_, i) => (
          <div className="p-downline-card" key={i}>
            <div className="p-downline-row">
              <div className="p-downline-info">
                <div className="skeleton" style={{ height: 12, width: "60%" }} />
                <div className="skeleton" style={{ height: 10, width: "40%", marginTop: 6 }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!downline || downline.length === 0) {
    return (
      <div className="p-downline-empty">
        <p>No referred drivers yet.</p>
        <span>Share your referral code to start building your downline.</span>
      </div>
    );
  }

  return (
    <div className="p-downline-list" role="tabpanel">
      {downline.map((d) => {
        const isOpen = expandedId === d.driver_ID;
        const requests = requestsById[d.driver_ID];
        const isLoadingThis = loadingRequests === d.driver_ID;
        const hasError = requestError === d.driver_ID;
        const requestCount = d.request_count ?? requests?.length ?? 0;

        return (
          <div className="p-downline-card" key={d.driver_ID}>
            <button
              className="p-downline-row"
              onClick={() => handleToggle(d.driver_ID)}
              aria-expanded={isOpen}
            >
              <div className="p-downline-info">
                <span className="p-downline-name">
                  <span className={`circle ${d.status === "online" ? "circle--online" : "circle--offline"}`}></span>
                  {d.driver_name || "—"}
                </span>

              </div>

              <div className="p-downline-meta">

                <span className="p-downline-count">
                  {requestCount} request{requestCount !== 1 ? "s" : ""}
                </span>
                <svg
                  className={`p-downline-chevron ${isOpen ? "p-downline-chevron--open" : ""}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="p-downline-requests">
                {isLoadingThis ? (
                  [...Array(2)].map((_, i) => (
                    <div className="p-downline-request-row" key={i}>
                      <div className="skeleton" style={{ height: 10, width: "20%" }} />
                      <div className="skeleton" style={{ height: 16, width: "25%" }} />
                      <div className="skeleton" style={{ height: 10, width: "30%" }} />
                    </div>
                  ))
                ) : hasError ? (
                  <p className="p-downline-no-requests">
                    Failed to load requests. Try again.
                  </p>
                ) : !requests || requests.length === 0 ? (
                  <p className="p-downline-no-requests">
                    No requests sent to this driver yet.
                  </p>
                ) : (
                  requests.map((r, index) => (
                    <div className="p-downline-request-row" key={r.request_id}>
                      <span className="p-downline-request-id">
                        ({index + 1})
                      </span>

                      <span className="p-downline-request-time">
                        {formatDateTime(r.responded_at || r.send_at)}
                      </span>
                      <span
                        className={`p-badge ${STATUS_BADGE[r.status?.toLowerCase()] || "p-badge--neutral"}`}
                      >
                        {r.status || "—"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}